/**
 * CoverageIndicator - Unified coverage display with navigation
 * Story 229: Navigate more easily between prd / design / tasks through capabilities
 *
 * Features:
 * - Shows "Coverage (N)" count badge
 * - Red when N=0, green when N>0
 * - Clickable dropdown showing linked items
 * - Each item navigates to target on click
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import './CoverageIndicator.css';

export interface CoverageItem {
  /** Unique identifier (anchor or task seq) */
  id: string;
  /** Primary display text */
  label: string;
  /** Secondary text (e.g., section title) */
  sublabel?: string;
}

interface CoverageIndicatorProps {
  /** Number of covering items */
  count: number;
  /** Items to show in dropdown */
  items: CoverageItem[];
  /** Label for the trigger button */
  label?: string;
  /** Called when an item is clicked for navigation */
  onNavigate?: (id: string) => void;
  /** Controls visibility (opacity) - for hover reveal behavior */
  visible?: boolean;
}

export function CoverageIndicator({
  count,
  items,
  label = 'Coverage',
  onNavigate,
  visible = true,
}: CoverageIndicatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleItemClick = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      onNavigate?.(id);
      setIsOpen(false);
    },
    [onNavigate]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, id: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        onNavigate?.(id);
        setIsOpen(false);
      }
    },
    [onNavigate]
  );

  // No coverage is a legitimate state - show nothing
  if (count === 0) {
    return null;
  }

  return (
    <div
      className="coverage-indicator"
      ref={dropdownRef}
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <button
        className="coverage-indicator__trigger"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="coverage-indicator__label">{label}</span>
        <span className="coverage-indicator__caret">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>

      {isOpen && items.length > 0 && (
        <div className="coverage-indicator__menu" role="listbox">
          {items.map((item) => (
            <div
              key={item.id}
              className="coverage-indicator__item"
              role="option"
              tabIndex={0}
              onClick={(e) => handleItemClick(e, item.id)}
              onKeyDown={(e) => handleKeyDown(e, item.id)}
            >
              {item.sublabel && (
                <span className="coverage-indicator__item-id">{item.sublabel}</span>
              )}
              <span className="coverage-indicator__item-label">{item.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
